import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { RefreshCw, ArrowRight, CalendarDays, FileText, Users, GraduationCap, Video, BarChart3, Settings2 } from "lucide-react";
import ManageDashboardFranchiseesDialog from "@/components/dashboard/ManageDashboardFranchiseesDialog";

// Feb-2026 Dashboard redesign per Sandra's brief:
//   • Full-width Journey overview (Interested + New franchisees)
//   • Compact Calendar · next 5 days + Contract renewals below
//   • Read-only mirror of Sales Pipeline / Launch Portal states
//   • Uses supplied icon-only SVGs — no runtime recolour, no substitutes

// ---- Step-id → icon filename maps (identical to journey component) ----

const INTERESTED_STEP_ORDER = [
  // Feb-2026 refinement: mirror the Sales Pipeline expanded Interested
  // journey exactly so the Dashboard icons and full tracker read
  // left-to-right in the same order.
  "territory_sent", "shadow_day_setup", "discovery_portal_sent",
  "confidentiality_agreement", "flex_information", "training_day_booked",
];

const INTERESTED_ICON_FILE = {
  discovery_portal_sent:     "discovery_sent.svg",
  shadow_day_setup:          "shadow_day_setup.svg",
  training_day_booked:       "training_day_booked.svg",
  territory_sent:            "territory_defined_and_sent.svg",
  confidentiality_agreement: "confidential_agreement_signed.svg",
  flex_information:          "flex_info_requested.svg",
};

const FRANCHISEE_STEP_ORDER = [
  "launch_portal_provided", "email_address_setup", "portal_account_setup",
  "mojo_business_facebook", "franchisee_facebook", "comfort_zone_invite",
  "contract_provided", "contract_length_and_plan", "territory_name_confirmed",
  "direct_debit_setup", "dbs_processed",
  "trolley_and_art", "apron_ordered", "printed_items",
];

const FRANCHISEE_ICON_FILE = {
  launch_portal_provided:   "launch_portal_provided.svg",
  email_address_setup:      "email_address_setup.svg",
  portal_account_setup:     "portal_setup.svg",
  mojo_business_facebook:   "business_facebook.svg",
  franchisee_facebook:      "franchise_facebook.svg",
  comfort_zone_invite:      "facebook_comfort_zone.svg",
  contract_provided:        "contract_provided.svg",
  contract_length_and_plan: "contract_length_and_plan.svg",
  territory_name_confirmed: "territory_name_confirmed.svg",
  direct_debit_setup:       "direct_debit_setup.svg",
  dbs_processed:            "dbs_processed.svg",
  trolley_and_art:          "trolleyart_materials.svg",
  apron_ordered:            "apron_ordered.svg",
  printed_items:            "printed_items_done.svg",
};

function iconSrc(kind, stepId, state) {
  const map = kind === "interested" ? INTERESTED_ICON_FILE : FRANCHISEE_ICON_FILE;
  const file = map[stepId];
  if (!file) return "";
  const color = state === "done" ? "green" : state === "na" ? "grey" : "orange";
  return `/journey-icons/mini/${kind}/${color}/${file}`;
}

// Small step-name label — falls back gracefully if the API doesn't return
// a label for a step (shouldn't happen but defensive).
function stepLabel(step) {
  return (step.label || step.id || "").replace(/_/g, " ").toUpperCase();
}

// ---- Journey icon row ---------------------------------------------------

function StepIcon({ kind, step }) {
  const label = stepLabel(step);
  return (
    <span
      role="img"
      tabIndex={0}
      aria-label={`${label} — ${step.state === "done" ? "Done" : step.state === "na" ? "N/A" : "To do"}`}
      title={label}
      data-testid={`dashboard-step-${kind}-${step.id}`}
      data-state={step.state}
      className="inline-flex items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/60"
      style={{ width: 32, height: 32 }}
    >
      <img
        src={iconSrc(kind, step.id, step.state)}
        alt=""
        draggable={false}
        style={{ width: 28, height: 28, display: "block" }}
      />
    </span>
  );
}

function JourneyIconRow({ kind, steps }) {
  const order = kind === "interested" ? INTERESTED_STEP_ORDER : FRANCHISEE_STEP_ORDER;
  const byId = Object.fromEntries((steps || []).map(s => [s.id, s]));
  const ordered = order
    .map(id => byId[id])
    .filter(Boolean);
  return (
    // Feb-2026 refinement: use full column width. The parent grid
    // assigns this column `minmax(0, 1fr)` so the icons spread across
    // the free space between the progress count and the percentage.
    // `justify-between` distributes the icons evenly; narrow viewports
    // still fall back to horizontal scroll via `overflow-x-auto`.
    <div className="flex flex-nowrap items-center justify-between gap-2 w-full overflow-x-auto">
      {ordered.map((s, i) => (
        <StepIcon key={s.id} kind={kind} step={s} />
      ))}
    </div>
  );
}

// ---- Journey row ---------------------------------------------------------

function JourneyRow({ kind, row }) {
  const isInterested = kind === "interested";
  const id = isInterested ? row.contact_id : row.franchisee_id;
  const href = isInterested
    ? `/contacts?tab=tabs&expand=${id}`
    : `/franchisees/${id}#section-launch-portal`;
  // Feb-2026 refinement: report "Complete" ONLY when every considered
  // step is Done (and at least one step is being considered). Anything
  // else — including a fresh journey with zero Done steps — stays
  // "In progress". Guards against a divide-by-zero "0/0 Complete".
  const isComplete = row.steps_considered > 0 && row.steps_done === row.steps_considered;
  return (
    <Link
      to={href}
      data-testid={`dashboard-journey-row-${kind}-${id}`}
      // Grid columns: name | progress count | icons (grows) | pct | arrow.
      // The icons column uses `minmax(0, 1fr)` so it stretches to fill
      // the empty gap that used to sit between the name and percentage.
      className="grid grid-cols-[minmax(180px,1fr)_auto_minmax(0,2fr)_auto_auto] items-center gap-4 rounded-xl px-4 py-3 hover:bg-white/60 transition-colors"
    >
      <div className="min-w-0">
        <div className="font-semibold text-stone-900 truncate">{row.name}</div>
      </div>
      <div className="text-xs">
        <div className="font-bold tabular-nums text-stone-900">
          {row.steps_done} / {row.steps_considered}
        </div>
        <div className="text-stone-500 uppercase tracking-wider text-[10px]">
          {isComplete ? "Complete" : "In progress"}
        </div>
      </div>
      <div className="min-w-0">
        <JourneyIconRow kind={kind} steps={row.steps} />
      </div>
      <div className="text-right">
        <div className="font-bold text-stone-900 tabular-nums">{row.percent_complete}%</div>
        <div className="text-[10px] uppercase tracking-wider text-stone-500">
          {isComplete ? "Complete" : "In progress"}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-stone-400" aria-hidden />
    </Link>
  );
}

// ---- Section shells ------------------------------------------------------

function JourneySection({ title, icon: Icon, tint, borderTint, headingClass, viewAllHref, viewAllLabel, rows, kind, loading, total, showAll, onToggleShowAll, onManage }) {
  const showToggle =
    typeof total === "number" && typeof onToggleShowAll === "function" &&
    total > 0 && (showAll ? rows.length > 5 : total > 5);
  return (
    <div
      className={`rounded-2xl border ${borderTint} ${tint} p-5`}
      data-testid={`dashboard-journey-section-${kind}`}
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className={`w-6 h-6 ${headingClass}`} />
          <h3 className={`text-2xl font-bold ${headingClass}`}>{title}</h3>
          {typeof onManage === "function" && (
            <button
              type="button"
              onClick={onManage}
              className={`ml-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${headingClass} border-current hover:bg-white/60`}
              data-testid={`dashboard-manage-${kind}`}
              title="Hide or restore individual franchisees on this list"
            >
              <Settings2 className="w-3.5 h-3.5" /> Manage
            </button>
          )}
        </div>
        <Link
          to={viewAllHref}
          className="text-sm font-semibold text-stone-700 hover:text-stone-900 flex items-center gap-1 shrink-0"
          data-testid={`dashboard-view-all-${kind}`}
        >
          {viewAllLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="divide-y divide-white/60">
        {loading && <div className="text-sm text-stone-500 px-4 py-3">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-stone-500 px-4 py-3">No records to show.</div>
        )}
        {!loading && rows.map(r => (
          <JourneyRow key={(kind === "interested" ? r.contact_id : r.franchisee_id)} kind={kind} row={r} />
        ))}
      </div>
      {showToggle && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={onToggleShowAll}
            data-testid={`dashboard-toggle-show-all-${kind}`}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${headingClass} border-current hover:bg-white/60`}
          >
            {showAll ? "Show fewer" : `Show all (${total})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Calendar panel ------------------------------------------------------

function fmtDayLabel(iso) {
  // Feb-2026 fix: accept both the shape used by the Dashboard-panel
  // helper (`ev.start_at`) and the canonical shape emitted by
  // ``/api/calendar/events`` which is ``ev.start`` (either an ISO
  // datetime or a `YYYY-MM-DD` all-day string). Previously Calendar
  // events landed with `ev.start_at === undefined`, so `new Date(undefined)`
  // produced the "Invalid Date / NaN / NaN" you saw on the live
  // Dashboard.
  if (!iso) return { dow: "—", day: "—", mon: "" };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { dow: "—", day: "—", mon: "" };
    const dow = d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
    const day = d.getDate();
    const mon = d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
    return { dow, day, mon };
  } catch { return { dow: "—", day: "—", mon: "" }; }
}

function _pickStart(ev) {
  // Canonical field is ``start`` (RFC3339 or YYYY-MM-DD); legacy
  // callers used ``start_at``. Never guess — return null if neither
  // is present so the caller can render an em-dash instead of NaN.
  return ev?.start || ev?.start_at || null;
}

function _pickEnd(ev) {
  return ev?.end || ev?.end_at || null;
}

function fmtTimeRange(ev) {
  try {
    const startIso = _pickStart(ev);
    const endIso = _pickEnd(ev);
    // All-day Google events use a `YYYY-MM-DD` string with no
    // time component — surface "All day" so we don't render
    // "00:00 – 00:00".
    if (startIso && /^\d{4}-\d{2}-\d{2}$/.test(startIso)) return "All day";
    const s = startIso ? new Date(startIso) : null;
    const e = endIso ? new Date(endIso) : null;
    const valid = (d) => d && !Number.isNaN(d.getTime());
    const t = (d) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    if (valid(s) && valid(e)) return `${t(s)} – ${t(e)}`;
    if (valid(s)) return t(s);
    return "";
  } catch { return ""; }
}

function CalendarPanel({ events, loading }) {
  // Feb-2026 fix: filter to genuinely-parsable events, then sort by
  // start ascending so the Dashboard always reads earliest → latest.
  // The upstream ``/api/calendar/events`` returns events already
  // sorted by Google but we don't trust it — a bad row (e.g. an
  // event with a malformed `start`) used to leak through as
  // "Invalid Date / NaN". Now those are dropped silently and the
  // panel shows the empty message if none survive.
  const upcoming = (events || [])
    .map((ev) => ({ ev, iso: _pickStart(ev) }))
    .filter(({ iso }) => {
      if (!iso) return false;
      const d = new Date(iso);
      return !Number.isNaN(d.getTime());
    })
    .sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime())
    .slice(0, 4)
    .map(({ ev }) => ev);
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="dashboard-calendar-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-blue-500" />
          <h3 className="text-xl font-bold text-stone-900">Calendar · next 5 days</h3>
        </div>
        <Link to="/calendar" className="text-sm font-semibold text-stone-700 hover:text-stone-900 flex items-center gap-1" data-testid="dashboard-open-calendar">
          Open calendar <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      {loading && <div className="text-sm text-stone-500">Loading…</div>}
      {!loading && upcoming.length === 0 && (
        <div className="text-sm text-stone-500" data-testid="dashboard-calendar-empty">
          Nothing scheduled in the next 5 days.
        </div>
      )}
      <div className="space-y-2">
        {upcoming.map(ev => {
          const startIso = _pickStart(ev);
          const { dow, day, mon } = fmtDayLabel(startIso);
          // Canonical API field is ``meeting_url``; older callers may
          // still surface ``join_url`` / ``location_url`` so we honour
          // all three.
          const joinUrl = ev.meeting_url || ev.join_url || ev.location_url;
          // Title = summary (Google's field name) OR title (our shape)
          // OR a stable fallback so we NEVER render "undefined" in a
          // customer-facing row.
          const title = ev.title || ev.summary || "Event";
          return (
            <div key={ev.id} className="flex items-center gap-4 py-2 border-t border-stone-100 first:border-0" data-testid={`dashboard-calendar-event-${ev.id}`}>
              <div className="flex-none w-16 text-center">
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{dow}</div>
                <div className="text-2xl font-bold text-stone-900 tabular-nums">{day}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{mon}</div>
              </div>
              <div className="flex-none text-xs text-stone-600 tabular-nums w-28">{fmtTimeRange(ev)}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">{title}</div>
                <div className="text-xs text-stone-500 truncate">{ev.location || ev.description || ""}</div>
              </div>
              {joinUrl && (
                <a href={joinUrl} target="_blank" rel="noreferrer"
                   className="flex-none px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-700 flex items-center gap-1"
                   data-testid={`dashboard-calendar-join-${ev.id}`}
                   onClick={(e) => e.stopPropagation()}>
                  <Video className="w-3 h-3" /> Join
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Renewals panel ------------------------------------------------------

function RenewalsPanel({ renewals, loading }) {
  const items = Array.isArray(renewals?.items) ? renewals.items : [];
  const upcoming = items
    .filter(r => typeof r.days_remaining === "number" && r.days_remaining >= 0)
    .sort((a, b) => a.days_remaining - b.days_remaining)
    .slice(0, 3);
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="dashboard-renewals-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-500" />
          <h3 className="text-xl font-bold text-stone-900">Contract renewals</h3>
        </div>
        <Link to="/renewals" className="text-sm font-semibold text-stone-700 hover:text-stone-900 flex items-center gap-1" data-testid="dashboard-open-renewals">
          Open renewals <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      {loading && <div className="text-sm text-stone-500">Loading…</div>}
      {!loading && upcoming.length === 0 && (
        <div className="text-sm text-stone-500">No renewals in the next year.</div>
      )}
      <div className="space-y-2">
        {upcoming.map(r => {
          const renewalDate = r.renewal_date ? new Date(r.renewal_date).toLocaleDateString("en-GB") : "";
          const org = r.franchisee?.organisation || r.organisation || "";
          const fname = [r.franchisee?.first_name, r.franchisee?.last_name].filter(Boolean).join(" ");
          return (
            <Link key={r.id} to={`/franchisees/${r.franchisee?.id || r.franchisee_id || ""}`}
              className="flex items-center gap-3 py-2 border-t border-stone-100 first:border-0 hover:bg-stone-50 -mx-2 px-2 rounded"
              data-testid={`dashboard-renewal-${r.id}`}>
              <div className="flex-none rounded bg-orange-100 text-orange-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
                {r.days_remaining} days left
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">{org}</div>
                <div className="text-xs text-stone-500 truncate">{fname}</div>
              </div>
              <div className="flex-none text-xs text-stone-500 tabular-nums hidden md:block">Renews {renewalDate}</div>
              <ArrowRight className="w-4 h-4 text-stone-400 flex-none" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ---- Legend --------------------------------------------------------------

function Legend() {
  const dot = (cls) => (<span className={`inline-block w-3 h-3 rounded-full ${cls}`} aria-hidden />);
  return (
    <div className="flex items-center gap-4 text-sm text-stone-700" data-testid="dashboard-journey-legend">
      <span className="flex items-center gap-2">{dot("bg-emerald-500")} Done</span>
      <span className="flex items-center gap-2">{dot("bg-orange-500")} To do</span>
      <span className="flex items-center gap-2">{dot("bg-slate-400")} N/A</span>
    </div>
  );
}

// ---- Page ---------------------------------------------------------------

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [renewals, setRenewals] = useState(null);
  const [renewalsLoading, setRenewalsLoading] = useState(true);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  // Feb-2026 Sandra brief: default the Interested section to the top 5,
  // but expose a "Show all (N)" / "Show fewer" toggle so the full
  // pipeline is one click away when she needs to eyeball everyone.
  const [showAllInterested, setShowAllInterested] = useState(false);
  // "Manage" dialog for the New-franchisees list (hide / restore per
  // row). Persistence lives server-side via the dashboard-visibility
  // PATCH, so toggles survive refresh without any local storage.
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setSummaryLoading(true);
      try {
        // ``interested_limit`` scales up only when the toggle is
        // expanded; franchisee slots stay pinned at 5.
        const params = { limit: 5 };
        if (showAllInterested) params.interested_limit = 50;
        const { data } = await api.get("/admin/mojo-journey/dashboard-summary", { params });
        if (!cancelled) setSummary(data);
      } catch (e) { if (!cancelled) setSummary({ interested: [], franchisees: [] }); }
      finally { if (!cancelled) setSummaryLoading(false); }
    }
    async function loadRenewals() {
      setRenewalsLoading(true);
      try {
        const { data } = await api.get("/contracts/renewals", { params: { within_days: 365 } });
        if (!cancelled) setRenewals(data);
      } catch (e) { if (!cancelled) setRenewals({ items: [] }); }
      finally { if (!cancelled) setRenewalsLoading(false); }
    }
    async function loadEvents() {
      setEventsLoading(true);
      try {
        const { data: st } = await api.get("/calendar/status");
        if (st?.connected) {
          const { data } = await api.get("/calendar/events", { params: { days_ahead: 5, days_back: 0 } });
          if (!cancelled) setEvents(Array.isArray(data?.events) ? data.events : (Array.isArray(data) ? data : []));
        } else if (!cancelled) setEvents([]);
      } catch (e) { if (!cancelled) setEvents([]); }
      finally { if (!cancelled) setEventsLoading(false); }
    }
    load(); loadRenewals(); loadEvents();
    return () => { cancelled = true; };
  }, [refreshTick, showAllInterested]);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Overview</div>
            <h1 className="text-3xl font-bold text-stone-900">Dashboard</h1>
          </div>
          <button
            onClick={() => setRefreshTick(t => t + 1)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-stone-700 hover:bg-white rounded-lg"
            data-testid="dashboard-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="dashboard-journey-overview">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-emerald-600" />
              <h2 className="text-2xl font-bold text-stone-900">Journey overview</h2>
            </div>
            <Legend />
          </div>

          <div className="space-y-4">
            <JourneySection
              title="Interested contacts"
              icon={Users}
              tint="bg-emerald-50/60"
              borderTint="border-emerald-100"
              headingClass="text-emerald-800"
              viewAllHref="/contacts?tab=tabs"
              viewAllLabel="View all interested contacts"
              kind="interested"
              rows={summary?.interested || []}
              loading={summaryLoading}
              total={typeof summary?.interested_total === "number" ? summary.interested_total : (summary?.interested?.length || 0)}
              showAll={showAllInterested}
              onToggleShowAll={() => setShowAllInterested(v => !v)}
            />
            <JourneySection
              title="New franchisees"
              icon={GraduationCap}
              tint="bg-orange-50/60"
              borderTint="border-orange-100"
              headingClass="text-orange-800"
              viewAllHref="/franchisees"
              viewAllLabel="View all new franchisees"
              kind="franchisee"
              rows={summary?.franchisees || []}
              loading={summaryLoading}
              onManage={() => setManageOpen(true)}
            />
          </div>
        </div>

        <ManageDashboardFranchiseesDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          onChanged={() => setRefreshTick((t) => t + 1)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CalendarPanel events={events} loading={eventsLoading} />
          <RenewalsPanel renewals={renewals} loading={renewalsLoading} />
        </div>
      </div>
    </div>
  );
}
